import { useCallback, useEffect, useState } from 'react';
import { Check, Info, X } from 'shared/icons';
import { LINK_STATUS_OPTIONS, MATCH_SOURCE_LABELS } from '../../constants/pricing';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import { FormatDecimal1, FormatInt, FormatMoney } from '../../utils/format';
import { IntelStatCard } from '../marketIntelligence/intelligenceShared';
import Button from '../ui/Button';
import ErrorCard from '../ui/ErrorCard';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { SkeletonTable } from '../ui/Skeleton';
import StandardTable from '../ui/StandardTable';
import { useToast } from '../ui/Toast';
import { DASH, FormatDay, LinkStatusPill, ProductPicker, SkuLink } from './pricingShared';

/**
 * DE-PARA DE FORNECEDORES — o produto do fornecedor (CNPJ + código da nota)
 * apontando para o NOSSO produto (SKU do Bling), com fator de conversão
 * (unidades nossas por unidade da nota). O importador cria a linha e sugere
 * o vínculo (EAN, código do fornecedor ou código = SKU); uma pessoa
 * confirma aqui. Como os lotes apontam para a linha, toda correção vale
 * para as notas antigas também.
 */

const DEFAULT_PAGE_SIZE = 30;

/** Fator editável inline (Enter/blur salva; Esc cancela). */
const FactorCell = ({ row, canWrite, onSaved }) => {
    const toast = useToast();
    const [value, setValue] = useState(String(row.conversion_factor ?? 1));
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        setValue(String(row.conversion_factor ?? 1));
    }, [row.conversion_factor]);
    const save = async () => {
        const parsed = Number(String(value).replace(',', '.'));
        if (!Number.isFinite(parsed) || parsed <= 0 || parsed === Number(row.conversion_factor)) {
            setValue(String(row.conversion_factor ?? 1));
            return;
        }
        setSaving(true);
        try {
            const updated = await api.updatePricingSupplierProduct(row.id, { conversion_factor: parsed });
            toast.success(`Fator de ${row.supplier_code} = ${FormatDecimal1(parsed)}`);
            onSaved?.(updated);
        } catch (error) {
            toast.error(error?.message || 'Não foi possível salvar o fator');
            setValue(String(row.conversion_factor ?? 1));
        } finally {
            setSaving(false);
        }
    };
    const suggested = row.conversion_suggested;
    return (
        <div className="text-right">
            {canWrite ? (
                <input
                    value={value}
                    onChange={(event) => setValue(event.target.value)}
                    onBlur={save}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.currentTarget.blur();
                        }
                        if (event.key === 'Escape') {
                            setValue(String(row.conversion_factor ?? 1));
                        }
                    }}
                    disabled={saving}
                    inputMode="decimal"
                    className="w-20 rounded-md border border-mono-300 bg-white px-2 py-1 text-right font-mono text-xs text-black dark:border-mono-700 dark:bg-mono-950 dark:text-white"
                    title="Unidades nossas por unidade da nota (caixa com 12 → 12)"
                />
            ) : (
                <span className="font-mono text-xs">{FormatDecimal1(row.conversion_factor)}</span>
            )}
            {suggested && Number(suggested) !== Number(row.conversion_factor) ? (
                <p
                    className="text-[11px] text-warning-700 dark:text-warning-400"
                    title="razão qTrib/qCom da última nota"
                >
                    nota sugere {FormatDecimal1(suggested)}
                </p>
            ) : null}
            {row.unit || row.unit_trib ? (
                <p className="text-[11px] text-mono-500">
                    {row.unit || '?'}
                    {row.unit_trib && row.unit_trib !== row.unit ? ` → ${row.unit_trib}` : ''}
                </p>
            ) : null}
        </div>
    );
};

const SupplierProductsPanel = () => {
    const toast = useToast();
    const { checkPermission } = useAuth();
    const canWrite = checkPermission('pricing:write');
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [supplier, setSupplier] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [error, setError] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);
    const [busyId, setBusyId] = useState(null);

    const refresh = useCallback(() => setRefreshTick((tick) => tick + 1), []);

    useEffect(() => {
        let cancelled = false;
        setError(null);
        api.getPricingSupplierProducts({
            search,
            status,
            supplier_cnpj: supplier,
            limit: pageSize,
            offset: (page - 1) * pageSize,
        })
            .then((payload) => {
                if (!cancelled) {
                    setRows(Array.isArray(payload?.items) ? payload.items : []);
                    setTotal(Number(payload?.total || 0));
                    setSummary(payload?.summary || null);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err?.message || 'Não foi possível carregar o de-para.');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setHasLoadedOnce(true);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [search, status, supplier, page, pageSize, refreshTick]);

    const applySearch = () => {
        const term = searchInput.trim();
        setSearchInput(term);
        if (term !== search) {
            setSearch(term);
            setPage(1);
        }
    };

    /** Atualiza a linha na tela sem refetch (o servidor já devolveu a linha nova). */
    const replaceRow = (updated) => {
        setRows((prev) => prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));
        refresh();
    };

    const update = async (row, payload, message) => {
        setBusyId(row.id);
        try {
            const updated = await api.updatePricingSupplierProduct(row.id, payload);
            toast.success(message);
            replaceRow(updated);
        } catch (err) {
            toast.error(err?.message || 'Não foi possível salvar');
        } finally {
            setBusyId(null);
        }
    };

    const columns = [
        {
            key: 'supplier',
            label: 'Fornecedor',
            getValue: (row) => `${row.supplier_name || ''} ${row.supplier_cnpj}`,
            render: (row) => (
                <div className="min-w-0 max-w-[200px]">
                    <p
                        className="truncate text-sm font-medium text-black dark:text-white"
                        title={row.supplier_name || ''}
                    >
                        {row.supplier_name || DASH}
                    </p>
                    <p className="font-mono text-[11px] text-mono-500">{row.supplier_cnpj}</p>
                </div>
            ),
        },
        {
            key: 'supplier_item',
            label: 'Produto do fornecedor',
            getValue: (row) => `${row.supplier_code} ${row.supplier_ean || ''} ${row.description || ''}`,
            render: (row) => (
                <div className="min-w-0 max-w-[280px]">
                    <p className="truncate text-sm" title={row.description || ''}>
                        {row.description || DASH}
                    </p>
                    <p className="font-mono text-[11px] text-mono-500">
                        cód. {row.supplier_code}
                        {row.supplier_ean ? ` · EAN ${row.supplier_ean}` : ''}
                    </p>
                </div>
            ),
        },
        {
            key: 'product',
            label: 'Nosso produto (Bling)',
            getValue: (row) => `${row.sku || ''} ${row.product?.title || ''}`,
            render: (row) =>
                row.sku ? (
                    <div className="min-w-0 max-w-[280px]">
                        <p>
                            <SkuLink sku={row.sku} />
                            {row.product && !row.product.exists ? (
                                <span className="ml-1 text-[11px] text-danger-600 dark:text-danger-400">
                                    (sumiu do cadastro)
                                </span>
                            ) : null}
                        </p>
                        <p className="truncate text-xs text-mono-500" title={row.product?.title || ''}>
                            {row.product?.title || DASH}
                            {row.product?.cost_price ? ` · custo Bling ${FormatMoney(row.product.cost_price)}` : ''}
                        </p>
                    </div>
                ) : canWrite ? (
                    <ProductPicker
                        disabled={busyId === row.id}
                        onSelect={(product) =>
                            update(row, { sku: product.sku }, `${row.supplier_code} → ${product.sku}`)
                        }
                    />
                ) : (
                    <span className="text-xs italic text-mono-500">sem vínculo</span>
                ),
        },
        {
            key: 'factor',
            label: 'Fator (un/nota)',
            align: 'right',
            getValue: (row) => Number(row.conversion_factor || 1),
            render: (row) => <FactorCell row={row} canWrite={canWrite} onSaved={replaceRow} />,
        },
        {
            key: 'status',
            label: 'Situação',
            getValue: (row) => row.status,
            render: (row) => (
                <div className="space-y-0.5">
                    <LinkStatusPill status={row.status} matchSource={row.match_source} />
                    <p className="text-[11px] text-mono-500">
                        {row.status === 'confirmado'
                            ? `confirmado em ${FormatDay(row.confirmed_at)}`
                            : row.match_source
                              ? MATCH_SOURCE_LABELS[row.match_source] || row.match_source
                              : 'nenhum candidato no cadastro'}
                    </p>
                </div>
            ),
        },
        {
            key: 'lots',
            label: 'Notas',
            align: 'center',
            getValue: (row) => Number(row.lots || 0),
            render: (row) => (
                <div className="text-center">
                    <span className="tabular-nums">{FormatInt(row.lots)}</span>
                    <p className="text-[11px] text-mono-500">
                        {row.last_emitted_at ? FormatDay(row.last_emitted_at) : DASH}
                    </p>
                </div>
            ),
        },
        ...(canWrite
            ? [
                  {
                      key: 'actions',
                      label: '',
                      sortable: false,
                      getValue: () => '',
                      render: (row) => (
                          <div className="flex justify-end gap-1">
                              {row.sku && row.status !== 'confirmado' ? (
                                  <Button
                                      size="sm"
                                      variant="primary"
                                      disabled={busyId === row.id}
                                      title="Confirmar o vínculo e o fator"
                                      onClick={() =>
                                          update(
                                              row,
                                              { status: 'confirmado' },
                                              `${row.supplier_code} → ${row.sku} confirmado`,
                                          )
                                      }
                                  >
                                      <span className="inline-flex items-center gap-1">
                                          <Check size={14} /> Confirmar
                                      </span>
                                  </Button>
                              ) : null}
                              {row.sku ? (
                                  <Button
                                      size="sm"
                                      variant="ghost"
                                      disabled={busyId === row.id}
                                      title="Desvincular (o produto do fornecedor fica sem SKU)"
                                      onClick={() => update(row, { sku: null }, `${row.supplier_code} desvinculado`)}
                                  >
                                      <X size={14} />
                                  </Button>
                              ) : null}
                          </div>
                      ),
                  },
              ]
            : []),
    ];

    const byStatus = summary?.by_status || {};

    return (
        <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-xl border border-info-200 bg-info-50 p-4 dark:border-info-800 dark:bg-info-900/20">
                <Info size={20} className="mt-0.5 shrink-0 text-info-600 dark:text-info-400" />
                <p className="text-sm text-info-800 dark:text-info-200">
                    Cada linha é um produto <strong>do fornecedor</strong> (CNPJ + código na nota) apontando para o{' '}
                    <strong>nosso produto do Bling</strong>. O importador sugere o vínculo pelo EAN, pelo código do
                    fornecedor ou quando o código já é o SKU; alguém confirma aqui. O <strong>fator</strong> converte a
                    unidade da nota em unidades nossas (caixa com 12 → 12) e vem sugerido pela unidade tributável do
                    XML. Corrigir o de-para recalcula <strong>todas</strong> as notas daquele produto.
                </p>
            </div>

            {summary ? (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <IntelStatCard
                        label="Sem vínculo"
                        value={FormatInt(byStatus.sem_vinculo || 0)}
                        sub="nenhum candidato no cadastro"
                        tone={byStatus.sem_vinculo ? 'danger' : 'mono'}
                        onClick={() => {
                            setStatus('sem_vinculo');
                            setPage(1);
                        }}
                    />
                    <IntelStatCard
                        label="Sugeridos a confirmar"
                        value={FormatInt(byStatus.sugerido || 0)}
                        sub="casados pelo importador"
                        tone={byStatus.sugerido ? 'warning' : 'mono'}
                        onClick={() => {
                            setStatus('sugerido');
                            setPage(1);
                        }}
                    />
                    <IntelStatCard
                        label="Confirmados"
                        value={FormatInt(byStatus.confirmado || 0)}
                        sub="validados por alguém"
                        tone="success"
                        onClick={() => {
                            setStatus('confirmado');
                            setPage(1);
                        }}
                    />
                    <IntelStatCard
                        label="Fornecedores"
                        value={FormatInt(summary.suppliers?.length || 0)}
                        sub={`${FormatInt(summary.suppliers?.reduce((sum, entry) => sum + entry.pending, 0) || 0)} pendência(s)`}
                    />
                </div>
            ) : null}

            <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[220px] max-w-md flex-1">
                    <Input
                        label="Buscar"
                        type="search"
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        onBlur={applySearch}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                applySearch();
                            }
                        }}
                        placeholder="Código, EAN, descrição, fornecedor ou SKU..."
                    />
                </div>
                <div className="w-56">
                    <Select
                        label="Situação"
                        value={status}
                        onChange={(event) => {
                            setStatus(event.target.value);
                            setPage(1);
                        }}
                    >
                        {LINK_STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </div>
                <div className="w-64">
                    <Select
                        label="Fornecedor"
                        value={supplier}
                        onChange={(event) => {
                            setSupplier(event.target.value);
                            setPage(1);
                        }}
                    >
                        <option value="">Todos</option>
                        {(summary?.suppliers || []).map((entry) => (
                            <option key={entry.supplier_cnpj} value={entry.supplier_cnpj}>
                                {entry.supplier_name || entry.supplier_cnpj} ({entry.total}
                                {entry.pending ? `, ${entry.pending} pend.` : ''})
                            </option>
                        ))}
                    </Select>
                </div>
            </div>

            {error ? (
                <ErrorCard
                    title="Erro ao carregar o de-para"
                    message={error}
                    action="Tentar novamente"
                    actionFn={refresh}
                />
            ) : !hasLoadedOnce ? (
                <SkeletonTable columns={7} rows={6} />
            ) : (
                <StandardTable
                    rows={rows}
                    columns={columns}
                    rowKey={(row) => row.id}
                    countLabel="produtos de fornecedor"
                    emptyMessage={
                        search || status || supplier
                            ? 'Nada no recorte.'
                            : 'Nenhum produto de fornecedor ainda — importe uma NF-e de compra.'
                    }
                    exportFileName="de-para-fornecedores"
                    searchEnabled={false}
                    paginationMode="server"
                    totalItems={total}
                    currentPage={page}
                    onPageChange={setPage}
                    itemsPerPage={pageSize}
                    onItemsPerPageChange={setPageSize}
                    defaultPageSize={DEFAULT_PAGE_SIZE}
                    pageSizeOptions={[30, 50, 100]}
                />
            )}
        </div>
    );
};

export default SupplierProductsPanel;
